/**
 * Replace [@DIS_{disclaimerId}:{displayName}] patterns in HTML strings with DisclaimerMarker components.
 * Returns JSX-like string that can be parsed by markdown/JSX parsers.
 *
 * @param html - HTML string containing disclaimer patterns
 * @returns HTML string with disclaimer patterns replaced with DisclaimerMarker components
 *
 * @example
 * parseHTMLWithDisclaimers('See disclaimer [@DIS_001:1] for details')
 * // Returns: 'See disclaimer <DisclaimerMarker disclaimerId="001" displayName="1" /> for details'
 */
export function parseHTMLWithDisclaimers(html: string): string {
  if (!html) return '';

  // Pattern: [@DIS_{disclaimerId}:{displayName}] or [@DIS_{disclaimerId}]
  // Matches: [@DIS_ followed by disclaimer ID (alphanumeric, spaces, underscores), optionally :displayName, then ]
  return html.replace(
    /\[@DIS_([^\]:]+)(?::([^\]]+))?\]/g,
    (_match, disclaimerId, displayName) => {
      const trimmedId = disclaimerId.trim();

      // If displayName is provided, use it; otherwise use the ID as displayName
      const validDisplayName = displayName ? displayName.trim() : trimmedId;

      if (!validDisplayName) return '';

      // Escape quotes in displayName and disclaimerId for JSX attributes
      const escapedDisplayName = validDisplayName.replace(/"/g, '&quot;');
      const escapedId = trimmedId.replace(/"/g, '&quot;');

      return `<DisclaimerMarker disclaimerId="${escapedId}" displayName="${escapedDisplayName}" />`;
    }
  );
}
